import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { DeployStatusBadge } from "@/components/DeployStatusBadge";
import { CookieConsent } from "@/components/cookie-consent";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/hooks/use-auth";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { reportAuthFailure } from "@/lib/auth-recovery";
import * as React from "react";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "CapitalDart: Find your next investor in minutes, not months" },
      { name: "description", content: "CapitalDart: find the right investors, reach the right contacts, and run a better capital raise. Curated 70,000+ institutional contact database with outreach written in your voice." },
      { name: "author", content: "Lovable" },
      { property: "og:title", content: "CapitalDart: Find your next investor in minutes, not months" },
      { property: "og:description", content: "CapitalDart: find the right investors, reach the right contacts, and run a better capital raise. Curated 70,000+ institutional contact database with outreach written in your voice." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@Lovable" },
      { name: "twitter:title", content: "CapitalDart: Find your next investor in minutes, not months" },
      { name: "twitter:description", content: "CapitalDart: find the right investors, reach the right contacts, and run a better capital raise. Curated 70,000+ institutional contact database with outreach written in your voice." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/d5258d67-044c-4355-961a-bbc4e61ab4d1" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/d5258d67-044c-4355-961a-bbc4e61ab4d1" },
      { name: "theme-color", content: "#0B2545" },
      { name: "google-site-verification", content: "NzrhrC0RZesMI1w1SbY4ya6LemlFgcQ53SeQdDn6cFg" },
    ],
    scripts: [
      {
        type: "text/javascript",
        children: `(function (w, d, s, p, t) { w.gr = w.gr || function () { w.gr.ce = 60; w.gr.q = w.gr.q || []; w.gr.q.push(arguments); }; p = d.getElementsByTagName(s)[0]; t = d.createElement(s); t.async = true; t.src = "https://script.getreditus.com/v2.js"; p.parentNode.insertBefore(t, p); })(window, document, "script"); gr("initCustomer", "247ffaf2-a865-4557-aa6a-f6e7620db902"); gr("track", "pageview");`,
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&family=Instrument+Serif:ital@0;1&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Spectral:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600&family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap",
      },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
  }),
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootComponent() {
  // Every query and mutation failure passes through here first. A 401 from a
  // server function means the session expired, so the shared recovery takes
  // over and sends the user to sign in instead of leaving a broken page.
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({ onError: (error) => reportAuthFailure(error) }),
        mutationCache: new MutationCache({ onError: (error) => reportAuthFailure(error) }),
      }),
  );
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <Outlet />
            <DeployStatusBadge />
            <CookieConsent />
            <Toaster richColors closeButton position="top-right" />
          </AuthProvider>
        </QueryClientProvider>
        <Scripts />
      </body>
    </html>
  );
}
